import { ChangeEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, MapPin, Save, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import {
  createJob,
  fetchAdminJobById,
  updateJob,
  uploadCareerImage,
} from '@/services/careerService';
import { geocodeLocation, JobInput, toJobSlug } from '@/types/career';
import JobLocationMap from '@/components/careers/JobLocationMap';

const jobSchema = z.object({
  title: z.string().min(2, 'Titre requis'),
  shortDescription: z.string().optional(),
  description: z.string().min(10, 'Description requise'),
  missions: z.string().min(5, 'Missions requises'),
  profile: z.string().min(5, 'Profil recherché requis'),
  offer: z.string().min(5, 'Offre requise'),
  location: z.string().min(2, 'Localisation requise'),
  mapLat: z.number().optional(),
  mapLng: z.number().optional(),
  employmentType: z.string().min(2, 'Type de contrat requis'),
  department: z.string().optional(),
  coverImage: z.string().optional(),
  isPublished: z.boolean().default(true),
});

type JobFormValues = z.infer<typeof jobSchema>;

const CONTRACT_TYPES = [
  'CDI',
  'CDD',
  'Stage',
  'Alternance',
  'Freelance',
  'Temps partiel',
  'Saisonnier',
] as const;

const JobEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const form = useForm<JobFormValues>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      title: '',
      shortDescription: '',
      description: '',
      missions: '',
      profile: '',
      offer: '',
      location: 'Hammamet, Tunisie',
      mapLat: undefined,
      mapLng: undefined,
      employmentType: 'CDI',
      department: '',
      coverImage: '',
      isPublished: true,
    },
  });

  const { data: existing, isLoading } = useQuery({
    queryKey: ['admin', 'careers', id],
    queryFn: () => fetchAdminJobById(id as string),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    form.reset({
      title: existing.title.fr || existing.title.en,
      shortDescription: existing.shortDescription.fr || existing.shortDescription.en,
      description: existing.description.fr || existing.description.en,
      missions: existing.missions,
      profile: existing.profile,
      offer: existing.offer,
      location: existing.location,
      mapLat: existing.mapLat,
      mapLng: existing.mapLng,
      employmentType: existing.employmentType,
      department: existing.department || '',
      coverImage: existing.coverImage || '',
      isPublished: existing.isPublished ?? true,
    });
  }, [existing, form]);

  const title = form.watch('title');
  const location = form.watch('location');
  const mapLat = form.watch('mapLat');
  const mapLng = form.watch('mapLng');
  const autoSlug = toJobSlug(title) || '…';

  const pinLocation = async () => {
    const query = form.getValues('location');
    if (!query.trim()) {
      toast({ title: 'Indiquez une adresse', variant: 'destructive' });
      return;
    }
    setIsGeocoding(true);
    try {
      const result = await geocodeLocation(query);
      if (!result) {
        toast({
          title: 'Adresse introuvable',
          description: 'Essayez une adresse plus précise.',
          variant: 'destructive',
        });
        return;
      }
      form.setValue('mapLat', result.lat, { shouldDirty: true });
      form.setValue('mapLng', result.lng, { shouldDirty: true });
      toast({ title: 'Position épinglée sur la carte' });
    } catch (error) {
      console.error(error);
      toast({ title: 'Échec de la localisation', variant: 'destructive' });
    } finally {
      setIsGeocoding(false);
    }
  };

  const mutation = useMutation({
    mutationFn: async (values: JobFormValues) => {
      if (!Number.isFinite(values.mapLat) || !Number.isFinite(values.mapLng)) {
        throw new Error('MAP_PIN_REQUIRED');
      }
      const payload: JobInput = {
        ...values,
        shortDescription: values.shortDescription?.trim() || '',
        mapLat: values.mapLat,
        mapLng: values.mapLng,
        department: values.department || undefined,
        coverImage: values.coverImage || undefined,
      };
      return isEdit ? updateJob(id as string, payload) : createJob(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'careers'] });
      queryClient.invalidateQueries({ queryKey: ['careers'] });
      toast({ title: isEdit ? 'Offre mise à jour' : 'Offre créée' });
      navigate('/admin/careers');
    },
    onError: (error) => {
      console.error(error);
      toast({
        title:
          error instanceof Error && error.message === 'MAP_PIN_REQUIRED'
            ? 'Épinglez la localisation sur la carte'
            : 'Échec de l’enregistrement',
        variant: 'destructive',
      });
    },
  });

  const handleCoverUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const url = await uploadCareerImage(file);
      form.setValue('coverImage', url);
      toast({ title: 'Image téléversée' });
    } catch (error) {
      console.error(error);
      toast({ title: 'Échec du téléversement', variant: 'destructive' });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Helmet>
        <title>{isEdit ? 'Modifier l’offre' : 'Nouvelle offre'} | Admin</title>
      </Helmet>

      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/careers">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Link>
        </Button>
        <h1 className="text-xl font-semibold">
          {isEdit ? 'Modifier l’offre' : 'Nouvelle offre'}
        </h1>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          className="space-y-5 rounded-2xl border border-border/70 bg-white p-5 shadow-soft"
        >
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Titre</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex. Designer d’intérieur" />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Slug automatique : <code className="font-mono">{autoSlug}</code>
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="employmentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type de contrat</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRACT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                      {field.value &&
                      !CONTRACT_TYPES.includes(
                        field.value as (typeof CONTRACT_TYPES)[number]
                      ) ? (
                        <SelectItem value={field.value}>{field.value}</SelectItem>
                      ) : null}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Département</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optionnel" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Localisation</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Adresse ou ville"
                      onChange={(e) => {
                        field.onChange(e);
                        form.setValue('mapLat', undefined);
                        form.setValue('mapLng', undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={pinLocation}
                disabled={isGeocoding || !location?.trim()}
              >
                {isGeocoding ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="mr-2 h-4 w-4" />
                )}
                Épingler sur la carte
              </Button>
              {Number.isFinite(mapLat) && Number.isFinite(mapLng) ? (
                <span className="text-xs text-muted-foreground">
                  Pin : {mapLat?.toFixed(5)}, {mapLng?.toFixed(5)}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Cliquez sur la carte (ou utilisez le bouton) pour placer le pin.
                </span>
              )}
            </div>
            <JobLocationMap
              location={location}
              lat={mapLat}
              lng={mapLng}
              editable
              onPinChange={(nextLat, nextLng) => {
                form.setValue('mapLat', nextLat, { shouldDirty: true });
                form.setValue('mapLng', nextLng, { shouldDirty: true });
              }}
              mapClassName="rounded-xl"
              showOpenLink={Number.isFinite(mapLat) && Number.isFinite(mapLng)}
            />
          </div>

          <FormField
            control={form.control}
            name="shortDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Courte description (optionnel)</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea rows={5} {...field} placeholder="Présentation du poste" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="missions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ce que vous ferez</FormLabel>
                <FormControl>
                  <Textarea rows={5} {...field} placeholder="Missions et responsabilités" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="profile"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Profil recherché</FormLabel>
                <FormControl>
                  <Textarea rows={5} {...field} placeholder="Compétences, expérience…" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="offer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ce que nous offrons</FormLabel>
                <FormControl>
                  <Textarea rows={5} {...field} placeholder="Avantages, environnement…" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coverImage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Image de couverture</FormLabel>
                <div className="space-y-3">
                  {field.value ? (
                    <img
                      src={field.value}
                      alt=""
                      className="h-40 w-full rounded-xl object-cover"
                    />
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" disabled={isUploading} asChild>
                      <label className="cursor-pointer">
                        {isUploading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        Téléverser
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleCoverUpload}
                        />
                      </label>
                    </Button>
                    <FormControl>
                      <Input {...field} placeholder="Ou coller une URL" />
                    </FormControl>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isPublished"
            render={({ field }) => (
              <FormItem className="flex items-center gap-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className="font-normal">Publié sur le site</FormLabel>
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="bg-furniture-gold text-black hover:bg-furniture-gold-hover"
            >
              {mutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Enregistrer
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default JobEditor;
